import { makeAutoObservable, runInAction, observable, toJS, } from 'mobx';
import { isArray } from 'lodash-es';
import { getLocale } from '@/utils/intl';
import storage from '@/utils/storage';
import queryString from 'query-string';
import AppController from '@/api/seller/Controller/AppController';
import UserController from '@/api/seller/Controller/UserController';

type MicroConfig = {
  url: string;
  baseroute: string;
  language: string;
  apiUrl: string;
  ufileCartDom: HTMLElement;
  menuPath: string;
  publicPath: string;
  pageTabContent: HTMLElement;
} | undefined;

type AppConfig = {
  /** WebDav 文件上传根目录 */
  webDavPath?: string;
  /** 文件下载链接 */
  fileDownloadPath: string;
  /** 用户id */
  userId?: string;
  /** 客户编码 */
  customerCode?: string;
  /** 客户名称 */
  customerName?: string;
  realName?: string;
  companyName?: string;
  /** 客户阶段
   * FORMAL：正式客户
   * POTENTIAL：意向客户
   */
  customerStageType?: 'FORMAL' | 'POTENTIAL';
  /**
   * 账户类型
   * MAIN：主账号
   * SUB：子账号
   */
  companySubsidiaryType?: 'MAIN' | 'SUB';
  /**
   *
   * Y：主关联账号
   * N：子关联账号
   */
  isMainRelation?: string;
  serviceAuthority?: string;
  bpartnerId?: string,
  token?: string,
  /** 公英制 */
  measurementSystem?: string;
  /** 多语言 */
  language?: string;
  /** eds 网站根目录 */
  edsUrl?: string;
  /** eds public 路径 */
  public?: string;
  /** help 网站根目录 */
  url: {
    HELP?: string;
    BOOKING?: string;
    USER_GUIDE?: string;
    WALLET_FEEDBACK_URL?: string;
    FMS?: string;
    FMS_EU?: string;
    APP?: string;
    TRACKING?: string;
    MANAGE?: string
  };
  /** 卖家上传目录 */
  manageUploadPath?: string;
  /** 美国用户 */
  isUsaUser?: string;
  /** 主菜单名称映射 */
  mainMenuNameMap?: {
    [key: string]: string;
  };
  /**新老菜单版本,分模块如{'merchan':'NEW','whinbound':'OLD'}**/
  menuVersion?: any;
  /** 商品条码批量打印最大数量 */
  maxBatchPrint?: number;
  /** FMS 上传目录配置 */
  fmsLocationName?: string;
  fmsOmsUploadLocation?: string;
  fmsUploadLocationUrls?: any, //中国区全部目录
  fmsUploadLocationUrlEUs?: any,//EU全部目录
  wechatOpenRedirectUri?: string; //微信扫码绑定账号 回调地址
  fmsUploadLocationUrlUSs?: any,//EU全部目录

  /** 平台配置 */
  platform: {
    /** ECA平台 */
    eca: string[];
    /** ECA退货平台 */
    ecaReturn: string[];
    wish: {
      GET_PAGE: string;
      CLIENT_ID: string;
    };
    shopify: {
      STORE_URL: string;
    };
  };
  /** 是否子账号 */
  isChildAccount?: boolean;

  IPSCheck?: boolean;
  OverseasCheck?: boolean;
  /** fms所有区域请求路径 */
  fmsUrlList?: string[];
  /** 是否打开异常单通知*/
  isOpenExceptionNofify?: string;
  /** 是否开启用户行为数据埋点*/
  isMonitorUserBehavior?: string;
  /**是否允许查看海外隐私数据 */
  isOverseasViewPermission?: string;
};

type Menu = {
  id: string;
  menu_url: string;
  menu_sort: string;
  'menu_zh-cn': string;
  'menu_zh-tw': string;
  'menu_en-us': string;
  _child?: Menu[];
};

class AppStore {
  microConfig: MicroConfig = undefined;
  hasLogin: boolean = false;
  locale: string = getLocale();
  token: string = '';
  config: AppConfig = {
    fileDownloadPath: '',
    url: {},
    platform: {
      eca: [],
      ecaReturn: [],
      wish: {
        GET_PAGE: '',
        CLIENT_ID: '',
      },
      shopify: {
        STORE_URL: '',
      },
    },
  };
  loading: boolean = false;
  menuTree: Menu[] = [];
  menuIdMap: { [key: string]: Menu } = {};
  menuList: Menu[] = [];
  pageElements: string[] = [];

  constructor() {
    makeAutoObservable(this, {
      menuTree: observable.shallow,
      menuIdMap: false,
      menuList: observable.shallow,
      pageElements: observable.shallow,
    });
  }

  setLocale(locale: string, forceRefresh: boolean = true) {
    const languageMatch = locale.match(/([a-zA-Z]+)/g) ?? [];
    const localeFormat = `${(languageMatch?.[0] ?? '').toLowerCase()}-${(languageMatch?.[1] ?? '').toUpperCase()}`;
    const think_language = languageMatch.map((s: string) => s.toLowerCase()).join('-');
    storage.set('locale', localeFormat);
    this.locale = localeFormat;

    // 嵌入eds自动刷新页面
    if (forceRefresh) {
      if (window.__MICRO_APP_ENVIRONMENT__) {
        window.location.href = queryString.stringifyUrl({
          url: window.location.href,
          query: {
            l: think_language
          }
        });
      } else {
        AppController.setLang({ l: think_language }, { throwError: false });
      }
    }
  }

  setMicroConfig(config: MicroConfig) {
    this.microConfig = config;
  }

  setToken(token: any) {
    console.log(token);
  }

  setAppConfig(config: AppConfig) {
    this.config = config;
    this.setLocale(config.language ?? '', false);
  }
  async refreshMenu(reloadSideNav: boolean = false) {
    if (window.__MICRO_APP_ENVIRONMENT__ && reloadSideNav) {
      window?.rawWindow?.top?.PageTabTool?.reloadSideNav?.();
      // return;
    }
    const { data: menu } = await AppController.getMenu();
    runInAction(() => {
      this.menuTree = isArray(menu) ? menu : [];
      this.menuIdMap = (function generateMenuMap(menuTree) {
        return menuTree.reduce((map: any, menu: any) => {
          map[menu.id] = menu;
          if (isArray(menu._child)) {
            Object.assign(map, generateMenuMap(menu._child));
          }
          return map;
        }, {});
      })(this.menuTree);
      const menuIdMap = toJS(this.menuIdMap);
      this.menuList = Object.keys(menuIdMap).map(id => ({
        ...this.menuIdMap[id],
        path: this.menuIdMap[id]?.menu_url+'/*',
      })).filter(d => d.menu_url);
    });
  }
  async queryAppConfig() {
    this.loading = true;
    const [
      { data: config },
    ] = await Promise.all([
      AppController.getAppConfig(),
      this.refreshMenu(),
      this.queryPageElements(),
    ]).finally(() => runInAction(() => this.loading = false));
    this.setAppConfig(config);
  }
  async queryPageElements() {
    const { data } = await UserController.ajaxGetRoleElements();
    runInAction(() => {
      this.pageElements = data?.menuElementArr ?? [];
    });
    return data;
  }

  async logout() {
    await UserController.logout();
  }
}

export default new AppStore();
